import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "../../../lib/utils.js";
import { Button } from "../../ui/button.js";
import { GlowCard } from "../../ui/glow-card.js";
import type { WidgetProps } from "../widget-registry.js";
import { AttendanceSkeleton } from "./widget-skeletons.js";
import { useAttendanceRange } from "./useAttendanceRange.js";
import {
  isoDate, parseIso, addDays, startOfWeek, startOfMonth,
  rangeFor, stepPeriod, periodsAgo, relativeLabel,
  weekLabel, weekLabelShort, monthLabel, monthLabelShort, dayLabel, dayLongLabel,
  leadingBlanks, weekdayNames, totalsFor,
  LETTER, STATUS_TEXT,
  type AttendanceDay, type DayStatus, type PeriodMode, type Range,
} from "./attendance-calendar.js";

// ─── The three states, on existing theme tokens only ──────────────────────────
// Each is a soft tint from a family plus the strong foreground from the same
// family, so both themes come for free and the text keeps its contrast.
const STATE_CLASS: Record<DayStatus, string> = {
  present:    "bg-[var(--color-success-bg-subtle)] text-[var(--color-success-fg)]",
  pending:    "bg-[var(--color-warning-bg-subtle)] text-[var(--color-warning-fg)]",
  absent:     "bg-[var(--color-error-bg-subtle)] text-[var(--color-error-fg)]",
  leave:      "bg-[var(--color-accent-bg-subtle)] text-[var(--color-accent-fg)]",
  // Holiday, weekly off, future and unknown are neutral and carry no letter.
  holiday:    "bg-surface-100 text-surface-400",
  weekly_off: "bg-surface-100 text-surface-400",
  future:     "bg-surface-100 text-surface-300",
  none:       "bg-surface-100 text-surface-300",
};

const SUMMARY_VALUE_CLASS: Record<"present" | "absent" | "leave", string> = {
  present: "text-[var(--color-success-fg)]",
  absent:  "text-[var(--color-error-fg)]",
  leave:   "text-[var(--color-accent-fg)]",
};

const FOCUS = "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500";

export function AttendanceWidget(_props: WidgetProps) {
  const today = useMemo(() => new Date(), []);
  const todayIso = isoDate(today);

  const [mode, setMode] = useState<PeriodMode>("week");
  const [anchor, setAnchor] = useState<Date>(today);
  const [selected, setSelected] = useState<string>(todayIso);
  const [fading, setFading] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);

  const range = useMemo(() => rangeFor(anchor, mode), [anchor, mode]);
  const { days, error, reload } = useAttendanceRange(range);
  const seenAnyRef = useRef(false);
  if (days !== null) seenAnyRef.current = true;
  const ago = useMemo(() => periodsAgo(anchor, mode, today), [anchor, mode, today]);
  const isCurrent = ago === 0;

  const byDate = useMemo(() => new Map((days ?? []).map(d => [d.date, d])), [days]);
  const totals = useMemo(() => totalsFor(days ?? []), [days]);
  const hasAny = (days ?? []).some(d => d.status === "present" || d.status === "absent" || d.status === "leave");

  /** A short fade on the main area whenever the view changes underneath it. */
  const withFade = (fn: () => void) => {
    setFading(true);
    fn();
    window.setTimeout(() => setFading(false), 220);
  };

  function switchMode(next: PeriodMode) {
    if (next === mode) return;
    withFade(() => { setMode(next); setAnchor(today); setSelected(todayIso); });
  }

  function step(delta: number) {
    const nextAnchor = stepPeriod(anchor, mode, delta);
    const nextRange = rangeFor(nextAnchor, mode);
    setAnchor(nextAnchor);
    // Keep the selection inside the period on show: today when it is visible,
    // else the last day there that actually has a state.
    if (selected < nextRange.from || selected > nextRange.to) {
      const inRange = todayIso >= nextRange.from && todayIso <= nextRange.to;
      if (inRange) setSelected(todayIso);
      else {
        // The next period's days may not have arrived yet; its first day is a
        // safe landing spot and the strip updates as soon as they do.
        const last = [...(days ?? [])].reverse()
          .find(d => d.date >= nextRange.from && d.date <= nextRange.to
                  && (d.status === "present" || d.status === "absent" || d.status === "leave"));
        setSelected(last?.date ?? nextRange.from);
      }
    }
  }

  function jumpToCurrent() {
    withFade(() => { setAnchor(today); setSelected(todayIso); });
  }

  /** Arrow keys walk the grid; Enter/Space is the button's own activation. */
  function onGridKeyDown(e: React.KeyboardEvent) {
    const stepBy = e.key === "ArrowLeft" ? -1 : e.key === "ArrowRight" ? 1
      : e.key === "ArrowUp" ? (mode === "week" ? -1 : -7)
      : e.key === "ArrowDown" ? (mode === "week" ? 1 : 7) : 0;
    if (stepBy === 0) return;
    e.preventDefault();
    const next = isoDate(addDays(parseIso(selected), stepBy));
    if (next < range.from || next > range.to) return;
    setSelected(next);
    gridRef.current?.querySelector<HTMLButtonElement>(`[data-day="${next}"]`)?.focus();
  }

  const selectedDay = byDate.get(selected);
  const label = mode === "week" ? weekLabel(range.from, range.to) : monthLabel(anchor);
  const shortLabel = mode === "week" ? weekLabelShort(range.from, range.to) : monthLabelShort(anchor);
  const weekdays = useMemo(() => weekdayNames(), []);

  const tooltip = (d: AttendanceDay) =>
    `${dayLongLabel(d.date)} — ${STATUS_TEXT[d.status]}${d.note ? ` · ${d.note}` : ""}`;

  // First load only: once any period has resolved, navigation keeps the chrome
  // and just fades the grid, rather than flashing a full skeleton on every step.
  const firstLoad = days === null && !error && !seenAnyRef.current;

  return (
    // spotlight="off": the calendar's day tiles carry their own status fills, and the
    // interior hover pool washed over them. The edge glow stays.
    <GlowCard glow="ultra" spotlight="off" size="none" radius="2xl" className="@container h-full p-[18px]">
      {firstLoad ? <AttendanceSkeleton /> : (<>
      {/* 1 ── Header */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <h3 className="text-[15px] font-bold text-surface-900">Attendance</h3>
        <div className="flex gap-1 rounded-lg bg-surface-100 p-0.5">
          {(["week", "month"] as const).map(m => (
            <button
              key={m}
              type="button"
              aria-pressed={mode === m}
              onClick={() => switchMode(m)}
              className={cn(
                "h-[26px] rounded-md px-2.5 text-[11px] font-semibold capitalize transition-colors motion-reduce:transition-none @max-[360px]:px-2 @max-[360px]:text-[10px]",
                FOCUS,
                mode === m
                  ? "bg-brand-600 text-white hover:bg-brand-700"
                  : "text-surface-600 hover:text-surface-900",
              )}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* 2 ── Period navigation. The label never wraps: on a narrow card it falls
          back to a year-less form rather than breaking onto three lines. */}
      <div className="mb-3 flex items-center gap-1.5">
        <Button
          type="button"
          variant="secondary"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={() => step(-1)}
          aria-label={`Previous ${mode}`}
        >
          <ChevronLeft size={15} />
        </Button>

        <div className="flex min-w-0 flex-1 items-baseline justify-center gap-1.5" aria-live="polite">
          <span className="truncate whitespace-nowrap text-[14px] font-bold text-surface-900">
            <span className="@max-[400px]:hidden">{label}</span>
            <span className="hidden @max-[400px]:inline">{shortLabel}</span>
          </span>
          {/* Dropped first on a narrow card, per the responsive rule. */}
          <span className="hidden whitespace-nowrap text-[11px] text-surface-500 @[340px]:inline">
            {relativeLabel(ago, mode)}
          </span>
        </div>

        <Button
          type="button"
          variant="secondary"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={() => step(1)}
          disabled={isCurrent}
          aria-label={`Next ${mode}`}
        >
          <ChevronRight size={15} />
        </Button>

        {!isCurrent && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 shrink-0 whitespace-nowrap px-2 text-[11px] text-brand-600"
            onClick={jumpToCurrent}
          >
            This {mode}
          </Button>
        )}
      </div>

      {/* 3 ── Summary */}
      <div className="mb-3 grid grid-cols-3 gap-2 @max-[360px]:mb-2 @max-[360px]:gap-1.5">
        {([["PRESENT", "present"], ["ABSENT", "absent"], ["LEAVE", "leave"]] as const).map(([text, k]) => (
          <div key={k} className="flex items-center justify-between gap-1 rounded-[10px] bg-surface-100 px-2.5 py-1.5 @max-[360px]:flex-col @max-[360px]:items-start @max-[360px]:gap-0">
            <span className="text-[10px] font-semibold uppercase tracking-[0.5px] text-surface-500">{text}</span>
            <span className={cn("text-[22px] font-bold leading-none @max-[360px]:text-[17px]", totals[k] > 0 ? SUMMARY_VALUE_CLASS[k] : "text-surface-400")}>
              {totals[k]}
            </span>
          </div>
        ))}
      </div>

      {/* 4 ── Main area: fixed height so Week and Month never resize the card */}
      <div
        ref={gridRef}
        onKeyDown={onGridKeyDown}
        className={cn(
          "flex h-[196px] flex-col justify-center transition-opacity duration-200 motion-reduce:transition-none @max-[360px]:h-[146px]",
          fading || days === null ? "opacity-0" : "opacity-100",
        )}
      >
        {error ? (
          <div className="text-center">
            <p className="mb-2 text-[12px] text-surface-500">Couldn't load your attendance.</p>
            <button type="button" onClick={reload} className={cn("rounded text-[12px] font-semibold text-brand-600 hover:underline", FOCUS)}>
              Try again
            </button>
          </div>
        ) : mode === "week" ? (
          <WeekView
            range={range} byDate={byDate} selected={selected} todayIso={todayIso}
            onSelect={setSelected} tooltip={tooltip}
          />
        ) : (
          <MonthView
            anchor={anchor} range={range} byDate={byDate} selected={selected} todayIso={todayIso}
            weekdays={weekdays} onSelect={setSelected} tooltip={tooltip}
          />
        )}
      </div>

      {days !== null && !error && !hasAny && (
        <p className="mt-1 text-center text-[11px] text-surface-400">No attendance recorded</p>
      )}

      {/* 5 ── Legend */}
      <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 @max-[360px]:mt-2 @max-[360px]:gap-x-2.5">
        {(["present", "absent", "leave"] as const).map(s => (
          <span key={s} className="flex items-center gap-1.5 text-[11px] text-surface-600">
            <span className={cn("flex h-[18px] w-[18px] items-center justify-center rounded text-[10px] font-bold", STATE_CLASS[s])}>
              {LETTER[s]}
            </span>
            {STATUS_TEXT[s]}
          </span>
        ))}
      </div>

      {/* 6 ── Selected day */}
      <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-surface-200 bg-surface-100 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="shrink-0 text-[13px] font-bold text-surface-900">{dayLabel(selected)}</span>
          <span className={cn(
            "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold",
            selectedDay ? STATE_CLASS[selectedDay.status] : "bg-surface-200 text-surface-500",
          )}>
            {selectedDay ? STATUS_TEXT[selectedDay.status] : "—"}
          </span>
        </div>
        {selectedDay?.note && (
          <span className="truncate text-[11px] text-surface-500 @max-[300px]:hidden">{selectedDay.note}</span>
        )}
      </div>

      {/* 7 ── Footer */}
      <div className="mt-2 text-right">
        <Link to="/attendance" className={cn("rounded text-[11px] font-semibold text-brand-600 no-underline hover:underline", FOCUS)}>
          Open attendance →
        </Link>
      </div>
      </>)}
    </GlowCard>
  );
}

// ─── Week: seven tall boxes ───────────────────────────────────────────────────

interface ViewProps {
  range: Range;
  byDate: Map<string, AttendanceDay>;
  selected: string;
  todayIso: string;
  onSelect: (d: string) => void;
  tooltip: (d: AttendanceDay) => string;
}

function WeekView({ range, byDate, selected, todayIso, onSelect, tooltip }: ViewProps) {
  const start = startOfWeek(parseIso(range.from));
  const dates = Array.from({ length: 7 }, (_, i) => isoDate(addDays(start, i)));

  return (
    <div className="grid grid-cols-7 gap-2">
      {dates.map(date => {
        const day = byDate.get(date) ?? { date, status: "none" as DayStatus };
        const d = parseIso(date);
        return (
          <button
            key={date}
            type="button"
            data-day={date}
            onClick={() => onSelect(date)}
            title={tooltip(day)}
            aria-label={`${dayLongLabel(date)}: ${STATUS_TEXT[day.status]}`}
            aria-current={date === todayIso ? "date" : undefined}
            className={cn("group flex flex-col items-center rounded-[10px]", FOCUS)}
          >
            <span className={cn(
              "flex h-[108px] w-full items-center justify-center rounded-[10px] text-[24px] font-bold @max-[360px]:aspect-square @max-[360px]:h-auto @max-[360px]:rounded-lg @max-[360px]:text-[15px] transition-transform duration-150 group-hover:-translate-y-0.5 motion-reduce:transform-none motion-reduce:transition-none",
              STATE_CLASS[day.status],
              date === todayIso && "outline outline-[1.5px] outline-brand-500",
              date === selected && "ring-2 ring-brand-600",
            )}>
              {LETTER[day.status] ?? ""}
            </span>
            <span className="mt-1 text-[10.5px] uppercase text-surface-500 @max-[360px]:text-[9px]">
              {d.toLocaleDateString(undefined, { weekday: "short" })}
            </span>
            <span className="text-[14px] text-surface-700 @max-[360px]:text-[11px]">{d.getDate()}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Month: compact 7-column grid ─────────────────────────────────────────────

function MonthView({
  anchor, range, byDate, selected, todayIso, weekdays, onSelect, tooltip,
}: ViewProps & { anchor: Date; weekdays: string[] }) {
  const first = startOfMonth(anchor);
  const blanks = leadingBlanks(anchor);
  const count = parseIso(range.to).getDate();
  const dates = Array.from({ length: count }, (_, i) => isoDate(addDays(first, i)));

  return (
    <div>
      <div className="mb-1 grid grid-cols-7 gap-1 @max-[360px]:gap-[3px]">
        {weekdays.map(w => (
          <span key={w} className="text-center text-[10.5px] uppercase text-surface-500">{w}</span>
        ))}
      </div>
      {/* 6 rows x 27px + 5 gaps x 4px + the header above still fits the 196px area. */}
      <div className="grid grid-cols-7 gap-1 @max-[360px]:gap-[3px]">
        {Array.from({ length: blanks }, (_, i) => <span key={`b${i}`} aria-hidden="true" />)}
        {dates.map(date => {
          const day = byDate.get(date) ?? { date, status: "none" as DayStatus };
          return (
            <button
              key={date}
              type="button"
              data-day={date}
              onClick={() => onSelect(date)}
              title={tooltip(day)}
              aria-label={`${dayLongLabel(date)}: ${STATUS_TEXT[day.status]}`}
              aria-current={date === todayIso ? "date" : undefined}
              className={cn(
                "flex h-[27px] items-center justify-between rounded-[7px] px-1.5 text-[11px] font-bold @max-[360px]:h-[20px] @max-[360px]:justify-center @max-[360px]:rounded @max-[360px]:px-0.5 @max-[360px]:text-[10px] transition-transform duration-150 hover:scale-[1.08] motion-reduce:transform-none motion-reduce:transition-none",
                STATE_CLASS[day.status],
                day.status === "future" && "opacity-60",
                date === todayIso && "outline outline-[1.5px] outline-brand-500",
                date === selected && "ring-2 ring-brand-600",
                FOCUS,
              )}
            >
              <span className="font-normal @max-[360px]:hidden">{parseIso(date).getDate()}</span>
              <span>{LETTER[day.status] ?? ""}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
